'use client'

import { useTranslation } from 'react-i18next'
import type { StorageAllocation } from '@garage/shared'
import { Dialog, Button, KeyValue } from '@/ui'
import { useFormatters } from '@/lib/hooks.js'
import { ActivityInfo } from '@/components/ActivityInfo.js'
import { AllocationPhoto } from './AllocationPhoto.js'

interface AllocationDetailDialogProps {
  open: boolean
  allocation: StorageAllocation | null
  onClose: () => void
  onEdit: () => void
  onDelete: () => void
}

/**
 * The full picture of one allocated item — bigger photo, quantity, notes,
 * and when it was added — reached by clicking its small icon in the level
 * grid rather than showing all of this on the icon itself.
 */
export function AllocationDetailDialog({ open, allocation, onClose, onEdit, onDelete }: AllocationDetailDialogProps) {
  const formatters = useFormatters()
  const { t } = useTranslation()
  if (!allocation) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={allocation.itemName}
      size="sm"
      actions={
        <div className="flex gap-3 justify-end">
          <Button variant="outlined" onClick={onClose}>
            {t('common.close')}
          </Button>
          <Button variant="outlined" onClick={onEdit}>
            {t('common.edit')}
          </Button>
          <Button variant="danger" onClick={onDelete}>
            {t('common.remove')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <AllocationPhoto allocation={allocation} size="lg" />
        <ActivityInfo entityType="storage_allocation" entityId={allocation.id} />
        <div>
          <KeyValue label={t('storage.quantity')} value={allocation.quantity} />
          {allocation.notes && <KeyValue label={t('storage.notes')} value={allocation.notes} numeric={false} />}
          <KeyValue label={t('storage.added')} value={formatters.dateTime(allocation.createdAt)} numeric={false} />
        </div>
      </div>
    </Dialog>
  )
}
